/**
 * Step 1: 角色设定
 * - 主角照片上传（圆形裁切，支持拖拽）
 * - 角色名称输入
 * - 角色性格标签（多选）
 * - 外观描述
 */
import { useRef, ChangeEvent, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useCreationStore } from '../../../store/useCreationStore';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import { Upload, Loader2, Wand2 } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const humanTraitOptions = [
  { id: 'brave', label: '勇敢', emoji: '🦁' },
  { id: 'kind', label: '善良', emoji: '💖' },
  { id: 'curious', label: '好奇', emoji: '🔍' },
  { id: 'playful', label: '调皮', emoji: '😜' },
  { id: 'clever', label: '聪明', emoji: '🧠' },
  { id: 'friendly', label: '友好', emoji: '🤝' },
];

const animalTraitOptions = [
  { id: 'brave', label: '勇敢', emoji: '🦁' },
  { id: 'kind', label: '善良', emoji: '💖' },
  { id: 'curious', label: '好奇', emoji: '🔍' },
  { id: 'playful', label: '调皮', emoji: '😜' },
  { id: 'clever', label: '聪明', emoji: '🧠' },
  { id: 'friendly', label: '友好', emoji: '🤝' },
  { id: 'agile', label: '敏捷', emoji: '⚡' },
  { id: 'gentle', label: '温顺', emoji: '🌸' },
  { id: 'alert', label: '警觉', emoji: '👀' },
  { id: 'greedy', label: '贪吃', emoji: '🍖' },
  { id: 'adventurous', label: '爱探险', emoji: '🎒' },
  { id: 'helpful', label: '爱帮助人', emoji: '🆘' },
];

const genderOptions = [
  { id: 'boy', labelHuman: '男孩', labelAnimal: '公', emoji: '♂️' },
  { id: 'girl', labelHuman: '女孩', labelAnimal: '母', emoji: '♀️' },
];

const speciesAppearanceHints: Record<string, string> = {
  rabbit: '长耳朵，圆滚滚的身体，尾巴像一团白棉花，三瓣嘴，门牙微微露出',
  fox: '尖尖的耳朵，蓬松的大尾巴，机灵的三角眼，橙红色的皮毛',
  bird: '彩色的羽毛，尖尖的小嘴，圆圆的眼睛，灵活的翅膀',
  bear: '圆耳朵，胖乎乎的身体，短短的尾巴，厚厚的脚掌，毛茸茸的',
  cat: '尖尖的耳朵，长长的胡须，柔软的毛发，灵活的长尾巴',
  dog: '耷拉的耳朵，湿漉漉的鼻子，摇摆的尾巴，忠诚的大眼睛',
  dinosaur: '长长的脖子或短短的爪子，背上有尖尖的脊刺，大大的嘴巴',
  whale: '光滑的蓝灰色皮肤，圆润的额头，扇形的尾巴，温和的大眼睛',
  bee: '黄黑相间的条纹身体，透明的翅膀，圆圆的肚子，可爱的触角',
  penguin: '黑白相间的羽毛，圆滚滚的肚子，短短的翅膀，摇摇摆摆的走路姿势',
  dolphin: '光滑的灰色皮肤，尖尖的嘴巴，弯曲的背鳍，友善的微笑',
  panda: '黑白相间的圆滚滚身体，大大的黑眼圈，圆圆的耳朵，憨态可掬',
  lion: '金黄色的鬃毛，威武的脸庞，强壮的身体，大大的爪子',
  tiger: '橙黑相间的条纹，圆圆的头，有力的四肢，威严的眼神',
  monkey: '棕色的毛发，长长的尾巴，圆圆的脸蛋，机灵的大眼睛',
  elephant: '大大的耳朵，长长的鼻子，粗壮的四条腿，灰色的厚皮肤',
  frog: '绿色的皮肤，鼓鼓的眼睛，圆圆的身体，有力的后腿',
  butterfly: '彩色的翅膀，纤细的身体，可爱的触角，轻盈的飞舞姿态',
  turtle: '圆圆的壳，短短的四肢，小小的脑袋，慢吞吞的走路',
  squirrel: '大大的尾巴，尖尖的耳朵，小小的爪子，灵活的动作',
  koala: '灰色的毛发，圆圆的大耳朵，黑色的鼻子，总是抱着树枝',
  giraffe: '长长的脖子，棕黄色的斑点，细细的腿，温和的眼神',
  owl: '圆圆的大脸盘，尖尖的耳朵羽毛，大大的眼睛，憨厚的模样',
  hedgehog: '小小的身体，布满尖刺的背，尖尖的鼻子，圆溜溜的眼睛',
  seal: '光滑的灰色皮毛，圆圆的身体，短短的鳍状肢，可爱的胡须',
};

const SPECIES_OPTIONS = [
  { id: 'rabbit', label: '小兔子', emoji: '🐰' },
  { id: 'fox', label: '小狐狸', emoji: '🦊' },
  { id: 'bird', label: '小鸟', emoji: '🐦' },
  { id: 'bear', label: '小熊', emoji: '🐻' },
  { id: 'cat', label: '小猫', emoji: '🐱' },
  { id: 'dog', label: '小狗', emoji: '🐶' },
  { id: 'dinosaur', label: '小恐龙', emoji: '🦖' },
  { id: 'whale', label: '小鲸鱼', emoji: '🐋' },
  { id: 'bee', label: '小蜜蜂', emoji: '🐝' },
  { id: 'penguin', label: '小企鹅', emoji: '🐧' },
  { id: 'dolphin', label: '小海豚', emoji: '🐬' },
  { id: 'panda', label: '小熊猫', emoji: '🐼' },
  { id: 'lion', label: '小狮子', emoji: '🦁' },
  { id: 'tiger', label: '小老虎', emoji: '🐯' },
  { id: 'monkey', label: '小猴子', emoji: '🐵' },
  { id: 'elephant', label: '小大象', emoji: '🐘' },
  { id: 'frog', label: '小青蛙', emoji: '🐸' },
  { id: 'butterfly', label: '小蝴蝶', emoji: '🦋' },
  { id: 'turtle', label: '小乌龟', emoji: '🐢' },
  { id: 'squirrel', label: '小松鼠', emoji: '🐿️' },
  { id: 'koala', label: '小考拉', emoji: '🐨' },
  { id: 'giraffe', label: '小长颈鹿', emoji: '🦒' },
  { id: 'owl', label: '小猫头鹰', emoji: '🦉' },
  { id: 'hedgehog', label: '小刺猬', emoji: '🦔' },
  { id: 'seal', label: '小海豹', emoji: '🦭' },
  { id: 'custom', label: '其他', emoji: '✨' },
];

const ageOptions = [
  { value: '3-4', label: '3-4岁', desc: '简单词汇，重复句式' },
  { value: '5-6', label: '5-6岁', desc: '基础对话，描述性词汇' },
  { value: '7-8', label: '7-8岁', desc: '复杂句式，丰富情感' },
];


export default function Step1Character() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    character,
    setCharacter,
    story,
    setStory,
    flow,
    supportingCharacters,
    addSupportingCharacter,
    removeSupportingCharacter,
    updateSupportingCharacter,
  } = useCreationStore();
  const isDIY = flow === 'diy';
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isGeneratingCard, setIsGeneratingCard] = useState(false);
  const isGeneratingRef = useRef(false);
  const isExtractingRef = useRef(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // 自动保存到数据库（debounce 2秒）
  useEffect(() => {
    const storyId = searchParams.get('storyId');
    if (!storyId) return;

    const timer = setTimeout(async () => {
      const token = localStorage.getItem('token');
      const store = useCreationStore.getState();
      const data = store.getCreationData();
      const currentCharacter = store.character;

      // 如果数据完全为空，跳过自动保存
      if (!currentCharacter.name && currentCharacter.traits.length === 0 && !currentCharacter.appearance && !currentCharacter.photoUrl) {
        return;
      }

      try {
        await fetch(`${API_BASE_URL}/stories/setup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            storyId,
            photoUrl: currentCharacter.photoUrl || '',
            appearance: currentCharacter.appearance || '',
            characterName: currentCharacter.name || '',
            characterType: currentCharacter.characterType,
            gender: currentCharacter.gender,
            species: currentCharacter.characterType === 'animal' ? (currentCharacter.species === 'custom' ? currentCharacter.customSpecies : currentCharacter.species) : undefined,
            interests: currentCharacter.traits || [],
            targetAge: story.age || '5-6',
            artStyle: data.visual.style || '',
            theme: data.story.theme || '',
            flow,
          }),
        });
        console.log('[Step1Character] Auto-saved to DB');
      } catch (err) {
        console.error('[Step1Character] Auto-save failed:', err);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [character.name, character.traits, character.appearance, character.photoUrl, character.characterType, character.gender, character.species, character.customSpecies, story.age, searchParams]);

  // 兜底恢复：如果 rehydrate 完成后数据仍为空但 URL 中有 storyId，从数据库恢复
  // 关键：必须等 persist rehydrate 完成后再判断，避免与 localStorage 竞争
  useEffect(() => {
    const doRecover = async () => {
      // 等待 rehydrate 完成（最多等 2 秒）
      let waited = 0;
      while (!useCreationStore.persist.hasHydrated() && waited < 20) {
        await new Promise(r => setTimeout(r, 100));
        waited++;
      }
      console.log('[Step1Character] Rehydrate status:', useCreationStore.persist.hasHydrated(), 'waited:', waited * 100, 'ms');

      const storyId = searchParams.get('storyId');
      const current = useCreationStore.getState().character;
      const isEmpty = !current.name && current.traits.length === 0 && !current.appearance && !current.photoUrl && !current.characterType && !current.gender;
      if (isEmpty && storyId) {
        console.log('[Step1Character] Data empty after rehydrate, recovering from DB...');
        recoverFromDB(storyId);
      } else if (storyId) {
        console.log('[Step1Character] Data already present after rehydrate, skip recover:', {
          hasName: !!current.name,
          hasPhoto: !!current.photoUrl,
          hasIdentityCard: !!current.identityCardUrl,
        });
      }
    };
    doRecover();
  }, []);

  const recoverFromDB = async (storyId: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success && result.data) {
        const story = result.data;
        const config = typeof story.config === 'string' ? JSON.parse(story.config) : (story.config || {});
        const characterData = typeof story.characterData === 'string' ? JSON.parse(story.characterData) : (story.characterData || {});

        // 优先保留本地已有数据（特别是 identityCardUrl，避免 rehydrate 后被 DB 空值覆盖）
        const current = useCreationStore.getState().character;
        const updates: Partial<typeof current> = {
          name: current.name || config.characterName || '',
          traits: current.traits.length > 0 ? current.traits : (story.interests || []),
          appearance: current.appearance || config.appearance || '',
          photoUrl: current.photoUrl || story.photoUrl || '',
          identityCardUrl: current.identityCardUrl || characterData?.identityCardUrl || '',
          characterType: current.characterType || config.characterType,
          gender: current.gender ?? config.gender ?? undefined,
          species: current.species || config.species || characterData?.species || undefined,
        };
        setCharacter(updates);

        // 恢复配角（若本地无数据则取数据库）
        const currentSC = useCreationStore.getState().supportingCharacters;
        if (currentSC.length === 0 && Array.isArray(story.supportingCharacters) && story.supportingCharacters.length > 0) {
          const recoveredSC = story.supportingCharacters.map((sc: any) => ({
            id: sc.id || `sc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            name: sc.name,
            species: sc.type || sc.species || 'animal',
            appearance: sc.appearance || '',
            relationship: sc.relationship || '',
            characterType: sc.characterType || 'animal',
          }));
          useCreationStore.setState({ supportingCharacters: recoveredSC });
          console.log('[Step1Character] Recovered supporting characters from DB:', recoveredSC.length);
        }

        console.log('[Step1Character] Auto-recovered from DB, merged with local state:', {
          identityCardFromLocal: !!current.identityCardUrl,
          identityCardFromDB: !!characterData?.identityCardUrl,
        });
      }
    } catch (err) {
      console.error('[Step1Character] Auto-recover failed:', err);
    }
  };

  const uploadPhoto = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        // 删除旧照片（同步删除腾讯云 COS 文件）
        if (character.photoUrl) {
          const oldFilename = character.photoUrl.split('?')[0].split('/').pop();
          if (oldFilename) {
            try {
              const delRes = await fetch(`${API_BASE_URL}/upload/${oldFilename}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
              });
              const delResult = await delRes.json();
              if (!delResult.success) {
                console.error('删除旧照片失败:', delResult.error);
              } else {
                console.log('[Step1] 旧照片已从 COS 删除:', oldFilename);
              }
            } catch (err) {
              console.error('删除旧照片请求失败:', err);
            }
          }
        }
        // 1. 保存 photoUrl 到前端 store
        setCharacter({ photoUrl: result.url });

        // 2. 同步到数据库（身份卡 API 需要从数据库读取 photoUrl）
        const storyId = searchParams.get('storyId');
        if (storyId) {
          const store = useCreationStore.getState();
          const data = store.getCreationData();
          await fetch(`${API_BASE_URL}/stories/setup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              storyId,
              photoUrl: result.url,
              appearance: character.appearance || '',
              characterName: character.name || '',
              characterType: character.characterType,
              gender: character.gender,
              species: character.characterType === 'animal' ? (character.species === 'custom' ? character.customSpecies : character.species) : undefined,
              interests: character.traits || [],
              targetAge: story.age || '5-6',
              artStyle: data.visual.style || '',
              theme: data.story.theme || '',
              flow: store.flow,
            }),
          }).catch(err => console.error('[Step1] Sync photoUrl to DB failed:', err));
        }

        // 3. 上传照片后不自动触发任何操作，等待用户手动点击提取按钮
        console.log('[Step1] Photo uploaded, waiting for user to click extract button');
      } else {
        alert('上传失败: ' + (result.error || '未知错误'));
      }
    } catch (error) {
      console.error('上传失败:', error);
      alert('照片上传失败，请重试');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('图片大小不能超过10MB');
      return;
    }
    // 本地预览（即时反馈）
    const reader = new FileReader();
    reader.onloadend = () => {
      setCharacter({
        photo: file,
        photoPreview: reader.result as string,
      });
    };
    reader.readAsDataURL(file);
    // 异步上传到 COS
    uploadPhoto(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCharacter({
          photo: file,
          photoPreview: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
      uploadPhoto(file);
    }
  };

  const toggleTrait = (traitId: string) => {
    const newTraits = character.traits.includes(traitId)
      ? character.traits.filter((t) => t !== traitId)
      : [...character.traits, traitId];
    setCharacter({ traits: newTraits });
  };

  const createStoryDraft = async (): Promise<string | null> => {
    try {
      const token = localStorage.getItem('token');
      const store = useCreationStore.getState();
      const data = store.getCreationData();
      const current = data.character;

      // 后端必填：targetAge, interests(非空), artStyle
      // 用户可能在 Step1 还没选性格，提供兜底默认值
      const interests = current.traits.length > 0 ? current.traits : ['friendly'];

      const payload = {
        userId: user?.id || '',
        pageCount: data.story.pages,
        targetAge: story.age || '5-6',
        theme: data.story.theme || '',
        artStyle: data.visual.style || '',
        interests,
        photoUrl: current.photoUrl || current.photoPreview || '',
        outline: data.story.outline || '',
        speed: data.story.speed,
        voiceId: data.visual.voice || 'xiaoyan',
        characterName: current.name || '',
        appearance: current.appearance || '',
        characterType: current.characterType,
        gender: current.gender,
        species: current.characterType === 'animal' ? (current.species === 'custom' ? current.customSpecies : current.species) : undefined,
        flow: store.flow,
      };
      const res = await fetch(`${API_BASE_URL}/stories/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.success && result.data?.storyId) {
        navigate(`/create?storyId=${result.data.storyId}&flow=${store.flow}`, { replace: true });
        console.log('[Step1] Story created:', result.data.storyId);
        return result.data.storyId;
      }
      console.warn('[Step1] Create story failed:', result.error);
      return null;
    } catch (err) {
      console.error('[Step1] Create story error:', err);
      return null;
    }
  };

  const generateIdentityCard = async (photoUrl?: string, appearance?: string) => {
    let storyId = searchParams.get('storyId');
    if (!storyId) {
      // 没有 storyId 时自动创建 story，然后再生成身份卡
      console.log('[Step1] No storyId, auto-creating story draft...');
      storyId = await createStoryDraft();
      if (!storyId) {
        alert('创建故事失败，请检查网络连接或填写完整信息后再试');
        return;
      }
    }
    const store = useCreationStore.getState();
    if (store.character.characterType === 'human' && !store.character.gender) {
      alert('请先选择角色性别，再生成身份卡');
      return;
    }

    const pUrl = photoUrl || character.photoUrl;
    if (!pUrl) {
      console.warn('[Step1] Skip identity card: no photoUrl');
      return;
    }

    // 确保有外观描述（优先使用传入参数，其次从最新 store 获取）
    const effectiveAppearance = (appearance || store.character.appearance || '').trim();
    console.log('[Step1] GenerateIdentityCard appearance:', {
      paramAppearance: JSON.stringify(appearance),
      storeAppearance: JSON.stringify(store.character.appearance),
      effectiveAppearance: JSON.stringify(effectiveAppearance),
    });
    if (!effectiveAppearance) {
      console.warn('[Step1] Skip identity card: no appearance description');
      return;
    }

    // 防重入：防止 uploadPhoto / extractAppearance / 按钮 同时触发多次
    if (isGeneratingRef.current) {
      console.log('[Step1] Skip identity card: generation already in progress');
      return;
    }
    isGeneratingRef.current = true;
    setIsGeneratingCard(true);

    console.log('[Step1] Starting identity card generation...', { storyId, appearanceLength: effectiveAppearance.length });

    try {
      const token = localStorage.getItem('token');
      const requestBody = { appearance: effectiveAppearance };
      console.log('[Step1] Identity card request body:', JSON.stringify(requestBody));
      const response = await fetch(`${API_BASE_URL}/characters/${storyId}/identity-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      const result = await response.json();
      if (result.success && result.data?.identityCardUrl) {
        setCharacter({ identityCardUrl: result.data.identityCardUrl });
        console.log('[Step1] Identity card generated:', result.data.identityCardUrl);
      } else {
        console.warn('[Step1] Identity card generation failed:', result.error);
      }
    } catch (error) {
      console.error('[Step1] Identity card error:', error);
    } finally {
      isGeneratingRef.current = false;
      setIsGeneratingCard(false);
    }
  };

  const extractAppearance = async () => {
    if (isExtractingRef.current) {
      console.log('[Step1] Extract double click blocked by ref lock');
      return;
    }
    isExtractingRef.current = true;

    if (!character.photoUrl) {
      alert('请先上传角色照片');
      isExtractingRef.current = false;
      return;
    }
    if (!character.gender) {
      alert('请先选择性别，再提取外观描述');
      isExtractingRef.current = false;
      return;
    }
    setIsExtracting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/characters/extract-appearance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          photoUrl: character.photoUrl,
          characterType: character.characterType,
          gender: character.gender,
          species: character.characterType === 'animal' ? (character.species === 'custom' ? character.customSpecies : character.species) : undefined,
          age: story.age,
        }),
      });
      const result = await response.json();
      console.log('[Step1] Extract API result:', result);
      if (result.success) {
        const extractedAppearance = result.data?.appearance?.trim();
        console.log('[Step1] Extracted appearance value:', JSON.stringify(extractedAppearance));
        if (!extractedAppearance) {
          console.warn('[Step1] Extracted appearance is empty, skipping identity card');
          alert('外观描述提取结果为空，请手动填写外观描述');
          return;
        }
        setCharacter({ appearance: extractedAppearance });
        // 不自动生生成身份卡，由用户手动点击「生成身份卡」按钮
      } else {
        alert('提取失败: ' + (result.error || '未知错误'));
      }
    } catch (error) {
      console.error('提取外观描述失败:', error);
      alert('提取外观描述失败，请手动填写');
    } finally {
      isExtractingRef.current = false;
      setIsExtracting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* 角色预览卡片 — 按设计规范：shadow-sm border border-slate-200 rounded-2xl */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 transition-all duration-200">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 text-center">
          {character.characterType === 'human' ? '人物预览' : character.characterType === 'animal' ? '动物预览' : '角色预览'}
        </h3>

        {/* 1. 原始照片 — 圆形头像，hover 有微妙反馈 */}
        <div className="flex justify-center mb-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="relative cursor-pointer group"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handlePhotoChange}
              className="hidden"
            />

            {character.photoUrl || character.photoPreview ? (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative w-28 h-28"
              >
                <img
                  src={character.photoUrl || character.photoPreview}
                  alt="原始照片"
                  className="w-full h-full rounded-full object-cover border-4 border-indigo-100 shadow-sm group-hover:border-indigo-300 transition-all duration-200"
                />
                {/* 悬停遮罩提示重新上传 */}
                <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/10 transition-all duration-200 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 drop-shadow" />
                </div>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const token = localStorage.getItem('token');
                    const storyId = searchParams.get('storyId');

                    // 1. 删除腾讯云 COS 上的文件
                    if (character.photoUrl) {
                      try {
                        const oldFilename = character.photoUrl.split('?')[0].split('/').pop();
                        if (oldFilename) {
                          const delRes = await fetch(`${API_BASE_URL}/upload/${oldFilename}`, {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${token}` },
                          });
                          const delResult = await delRes.json();
                          if (!delResult.success) {
                            console.error('删除照片失败:', delResult.error);
                            alert('照片已从页面移除，但腾讯云 COS 文件删除失败，请检查网络或联系管理员');
                          } else {
                            console.log('[Step1] 照片已从 COS 删除:', oldFilename);
                          }
                        }
                      } catch (err) {
                        console.error('删除照片请求失败:', err);
                        alert('照片已从页面移除，但腾讯云 COS 文件删除请求失败');
                      }
                    }

                    // 2. 清空前端状态
                    setCharacter({ photo: undefined, photoPreview: '', photoUrl: '', identityCardUrl: '' });

                    // 3. 同步到数据库（防止刷新后从数据库恢复旧照片）
                    if (storyId) {
                      const store = useCreationStore.getState();
                      const data = store.getCreationData();
                      try {
                        await fetch(`${API_BASE_URL}/stories/setup`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                          },
                          body: JSON.stringify({
                            storyId,
                            photoUrl: '',
                            appearance: store.character.appearance || '',
                            characterName: store.character.name || '',
                            characterType: store.character.characterType,
                            gender: store.character.gender,
                            species: store.character.characterType === 'animal' ? (store.character.species === 'custom' ? store.character.customSpecies : store.character.species) : undefined,
                            interests: store.character.traits || [],
                            targetAge: story.age || '5-6',
                            artStyle: data.visual.style || '',
                            theme: data.story.theme || '',
                            flow: store.flow,
                          }),
                        });
                        console.log('[Step1Character] Photo deletion synced to DB');
                      } catch (err) {
                        console.error('[Step1Character] Sync photo deletion to DB failed:', err);
                      }
                    }
                  }}
                  className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-rose-600 transition-colors shadow-md"
                >
                  <span className="leading-none">×</span>
                </button>
              </motion.div>
            ) : (
              <div className="w-28 h-28 rounded-full border-2 border-dashed border-slate-300 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50/50 transition-all duration-200 flex flex-col items-center justify-center cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mb-1">
                  {isUploading ? (
                    <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5 text-indigo-500" />
                  )}
                </div>
                <p className="text-xs text-slate-500 font-medium">
                  {character.characterType === 'animal'
                    ? '上传参考图（可选）'
                    : isDIY
                      ? '上传照片（可选）'
                      : '上传照片'
                  }
                </p>
                {character.characterType === 'animal' && (
                  <p className="text-[10px] text-slate-400 mt-1 text-center px-2">
                    可上传宠物照片或喜欢的动物图片
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 2. 身份卡预览 — 卡片样式，与上传区独立 */}
        {character.identityCardUrl && (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mb-4"
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs font-medium text-slate-500">角色身份卡</span>
              <span className="text-[10px] text-slate-400">用于绘本插图一致性</span>
            </div>
            <div className="relative w-full aspect-square max-w-[200px] mx-auto rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white hover:shadow-md hover:border-slate-300 transition-all duration-200">
              <img
                src={character.identityCardUrl}
                alt="身份卡"
                className="w-full h-full object-contain"
              />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[10px] font-medium px-2.5 py-0.5 rounded-full shadow-sm">
                {character.characterType === 'animal' && character.species && character.species !== 'custom'
                  ? `${SPECIES_OPTIONS.find((s: {id: string; label: string; emoji: string}) => s.id === character.species)?.label || '动物'} · 身份卡`
                  : '身份卡'
                }
              </div>
            </div>
          </motion.div>
        )}

        {/* 3. 生成身份卡按钮（仅未生成时显示，且已上传照片） */}
        {character.photoUrl && !character.identityCardUrl && (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              size="sm"
              isLoading={isGeneratingCard}
              leftIcon={<Wand2 className="w-3.5 h-3.5" />}
              disabled={isGeneratingCard || !character.characterType || !character.gender}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                generateIdentityCard(character.photoUrl, character.appearance);
              }}
            >
              {!character.characterType
                ? '请先选择角色类型'
                : !character.gender
                  ? '请先选择性别'
                  : '生成身份卡'}
            </Button>
          </div>
        )}
      </div>

      {/* 角色类型 */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          角色类型 <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'human', label: '人物', emoji: '👧', desc: '人、孩子等' },
            { id: 'animal', label: '动物', emoji: '🐰', desc: '兔子、熊等' },
          ].map((type) => (
            <motion.button
              key={type.id}
              onClick={() => setCharacter({ characterType: type.id as 'human' | 'animal' })}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                rounded-xl border-2 transition-all duration-200
                flex items-center gap-3
                ${character.characterType === type.id
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30'
                }
              `}
              style={{ padding: '12px 16px' }}
            >
              <span className="text-2xl">{type.emoji}</span>
              <div className="text-left">
                <div className="text-sm font-medium">{type.label}</div>
                <div className="text-xs text-slate-400">{type.desc}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* 物种选择（仅动物类型） */}
      {character.characterType === 'animal' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          style={{ padding: '8px 0' }}
        >
          <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
            物种 <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {SPECIES_OPTIONS.map((species) => (
              <motion.button
                key={species.id}
                onClick={() => setCharacter({ species: species.id, customSpecies: species.id === 'custom' ? '' : undefined })}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  rounded-xl border-2 transition-all duration-200
                  flex flex-col items-center gap-1
                  ${character.species === species.id
                    ? 'border-amber-400 bg-amber-50 text-amber-700'
                    : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30'
                  }
                `}
                style={{ padding: '10px 8px' }}
              >
                <span className="text-xl">{species.emoji}</span>
                <span className="text-xs font-medium">{species.label}</span>
              </motion.button>
            ))}
          </div>
          {character.species === 'custom' && (
            <input
              type="text"
              value={character.customSpecies || ''}
              onChange={(e) => setCharacter({ customSpecies: e.target.value })}
              placeholder="请输入物种，如：小松鼠"
              className="w-full mt-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
            />
          )}
        </motion.div>
      )}

      {/* 性别选择（人物/动物都显示） */}
      {character.characterType && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          style={{ padding: '8px 0' }}
        >
          <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
            性别 <span className="text-red-500">*</span>
          </label>
          <div className={`grid grid-cols-2 gap-3 rounded-xl p-1 transition-all ${!character.gender && character.photoUrl ? 'bg-rose-50 border-2 border-rose-300' : ''}`}>
            {genderOptions.map((gender) => (
              <motion.button
                key={gender.id}
                onClick={() => {
                  const currentGender = character.gender;
                  const newGender = gender.id as 'boy' | 'girl';
                  if (currentGender && currentGender !== newGender) {
                    // 切换性别时清空外观描述和身份卡，强制重新提取
                    setCharacter({ gender: newGender, appearance: '', identityCardUrl: '' });
                  } else {
                    setCharacter({ gender: newGender });
                  }
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  rounded-xl border-2 transition-all duration-200
                  flex items-center gap-3
                  ${character.gender === gender.id
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30'
                  }
                `}
                style={{ padding: '12px 16px' }}
              >
                <span className="text-2xl">{gender.emoji}</span>
                <span className="text-sm font-medium">
                  {character.characterType === 'animal' ? gender.labelAnimal : gender.labelHuman}
                </span>
              </motion.button>
            ))}
          </div>
          {!character.gender && character.photoUrl && (
            <p className="text-xs text-rose-500 mt-1.5 text-center">
              请先选择性别，再提取外观描述
            </p>
          )}
        </motion.div>
      )}

      {/* 故事基础设置分组 */}
      <div className="border-t border-slate-100 pt-4 mt-2">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm">📖</span>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">故事基础设置</span>
        </div>
      </div>

      {/* 目标年龄（前置到 Step1，Step2 同步显示） */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          目标年龄 <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-3 gap-3">
          {ageOptions.map((age) => (
            <motion.button
              key={age.value}
              onClick={() => setStory({ age: age.value })}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                rounded-xl border-2 transition-all duration-200 text-center
                ${story.age === age.value
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-slate-200 hover:border-amber-200 hover:bg-amber-50/30'
                }
              `}
              style={{ padding: '12px 8px' }}
            >
              <div className="text-sm font-bold">{age.label}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{age.desc}</div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* 角色名称 */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          角色名称 <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={character.name}
          onChange={(e) => setCharacter({ name: e.target.value })}
          placeholder="给主角起个名字，如 Benny"
          className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-base"
          style={{ height: '48px', padding: '8px' }}
        />
      </div>

      {/* 性格标签 */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          性格特点 <span className="text-red-500">*</span>
          <span className="text-slate-400 font-normal ml-2">可多选</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(character.characterType === 'animal' ? animalTraitOptions : humanTraitOptions).map((trait) => (
            <motion.button
              key={trait.id}
              onClick={() => toggleTrait(trait.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                rounded-xl border-2 transition-all duration-200
                flex flex-col items-center gap-2
                ${character.traits.includes(trait.id)
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30'
                }
              `}
              style={{ padding: '8px' }}
            >
              <span className="text-2xl">{trait.emoji}</span>
              <span className="text-sm font-medium">{trait.label}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* 外观描述 */}
      <div style={{ padding: '8px 0' }}>
        <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
          <label className="block text-sm font-semibold text-slate-700">
            外观描述 <span className="text-red-500">*</span>
            <span className="text-slate-400 font-normal ml-2">
              {character.appearance.trim() ? '已填写，可继续修改' : (isDIY ? '请详细描述，帮助 AI 画出一致的角色' : '必须填写，AI提取后请确认')}
            </span>
          </label>
        </div>

        {/* AI 提取按钮：上传照片后可选点击，提取后变灰禁用 */}
        {character.photoUrl && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
          >
            <motion.button
              onClick={extractAppearance}
              disabled={isExtracting || !!character.appearance.trim() || !character.characterType || !character.gender}
              whileHover={{ scale: (!character.appearance.trim() && !!character.characterType && !!character.gender) ? 1.02 : 1 }}
              whileTap={{ scale: (!character.appearance.trim() && !!character.characterType && !!character.gender) ? 0.98 : 1 }}
              className={`
                w-full flex items-center justify-center gap-2 font-semibold rounded-xl transition-all
                ${isExtracting 
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                  : character.appearance.trim()
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : !character.characterType || !character.gender
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:from-violet-600 hover:to-indigo-700'
                }
              `}
              style={{ padding: '14px 20px' }}
            >
              {isExtracting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Wand2 className="w-5 h-5" />
              )}
              {isExtracting 
                ? 'AI 正在分析照片，请稍候...' 
                : character.appearance.trim() 
                  ? '✓ 已提取外观描述' 
                  : !character.characterType
                    ? '请先选择角色类型'
                    : !character.gender
                      ? '请先选择性别'
                      : '✨ AI 提取外观描述'}
            </motion.button>
            <p className={`text-xs mt-2 text-center ${character.appearance.trim() ? 'text-emerald-600' : 'text-amber-600'}`}>
              {character.appearance.trim() 
                ? '外观描述已提取，请确认无误后，点击下方「生成身份卡」按钮生成角色肖像'
                : !character.characterType
                  ? '请先选择角色类型后，再提取外观描述'
                  : !character.gender
                    ? '请选择性别后，再提取外观描述'
                    : (isDIY 
                      ? '上传照片后，可点击上方按钮让 AI 自动提取外观描述；你也可以直接在下方手动填写'
                      : '上传照片后，请点击上方按钮提取角色外观描述，确认无误后可进入下一步')
              }
            </p>
          </motion.div>
        )}

        <textarea
          value={character.appearance}
          onChange={(e) => setCharacter({ appearance: e.target.value })}
          placeholder={(() => {
            const hint = character.characterType === 'animal' && character.species && character.species !== 'custom'
              ? speciesAppearanceHints[character.species]
              : null;
            if (character.characterType === 'animal') {
              return isDIY
                ? `详细描述动物的外貌，帮助 AI 画得一致。${hint ? '如：' + hint + '...' : '如：毛色、体型、标志性特征...'}`
                : `描述动物的外貌特征。${hint ? '如：' + hint + '...' : '如：毛色、体型、标志性特征...'}`;
            }
            return isDIY
              ? "详细描述角色的外貌，帮助 AI 画得一致。如：圆圆的脸蛋，棕色短发，红色T恤和蓝色短裤，黑色大眼睛，总是带着微笑..."
              : "描述角色的外貌特征，如：圆圆的脸蛋，棕色短发，红色T恤和蓝色短裤...";
          })()}
          className={`
            w-full rounded-xl focus:outline-none focus:ring-2 transition-all resize-none text-base
            ${character.appearance.trim() 
              ? 'bg-white border-2 border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500/20' 
              : 'bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20'
            }
          `}
          style={{ minHeight: '96px', padding: '8px' }}
        />

        {/* 生成身份卡按钮：放在外观描述附近，仅未生成时显示，且已上传照片 */}
        {character.photoUrl && character.appearance.trim() && !character.identityCardUrl && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3"
          >
            <Button
              variant="primary"
              size="md"
              isLoading={isGeneratingCard}
              leftIcon={<Wand2 className="w-4 h-4" />}
              disabled={isGeneratingCard || !character.characterType || !character.gender}
              onClick={() => generateIdentityCard(character.photoUrl, character.appearance)}
              className="w-full"
            >
              {!character.characterType
                ? '请先选择角色类型'
                : !character.gender
                  ? '请先选择性别'
                  : '生成身份卡'}
            </Button>
            <p className="text-xs text-amber-600 mt-1.5 text-center">
              角色身份卡用于绘本插图一致性，请务必生成
            </p>
          </motion.div>
        )}
      </div>

      {/* 配角管理（AI 和 DIY 模式都显示） */}
      <SupportingCharacterSection
        characters={supportingCharacters}
        onAdd={addSupportingCharacter}
        onRemove={removeSupportingCharacter}
        onUpdate={updateSupportingCharacter}
        protagonistType={character.characterType}
      />
    </div>
  );
}

// =============================================================================
// SupportingCharacterSection - 配角管理组件
// =============================================================================

interface SupportingCharacterSectionProps {
  characters: Array<{ id: string; name: string; species: string; appearance: string; relationship?: string; characterType?: 'human' | 'animal' }>;
  onAdd: (char: { id: string; name: string; species: string; appearance: string; relationship?: string; characterType?: 'human' | 'animal' }) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, data: Partial<{ name: string; species: string; appearance: string; relationship?: string; characterType?: 'human' | 'animal' }>) => void;
  protagonistType?: 'human' | 'animal';
}

function SupportingCharacterSection({ characters, onAdd, onRemove, onUpdate, protagonistType = 'human' }: SupportingCharacterSectionProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    species: string;
    customSpecies: string;
    appearance: string;
    relationship: string;
    characterType: 'human' | 'animal';
  }>({
    name: '',
    species: '',
    customSpecies: '',
    appearance: '',
    relationship: '',
    characterType: protagonistType,
  });

  const resetForm = () => {
    setFormData({ name: '', species: '', customSpecies: '', appearance: '', relationship: '', characterType: protagonistType });
    setIsAdding(false);
    setEditingId(null);
  };

  const handleSave = () => {
    const species = formData.species === 'custom' ? formData.customSpecies : formData.species;
    if (!formData.name.trim() || !species.trim() || !formData.appearance.trim()) return;

    const data = {
      id: editingId || `sc-${Date.now()}`,
      name: formData.name.trim(),
      species: species.trim(),
      appearance: formData.appearance.trim(),
      relationship: formData.relationship.trim() || undefined,
      characterType: formData.characterType,
    };

    if (editingId) {
      onUpdate(editingId, data);
    } else {
      onAdd(data);
    }
    resetForm();
  };

  const handleEdit = (char: typeof characters[0]) => {
    const isCustom = !SPECIES_OPTIONS.find((s) => s.id === char.species);
    setFormData({
      name: char.name,
      species: isCustom ? 'custom' : char.species,
      customSpecies: isCustom ? char.species : '',
      appearance: char.appearance,
      relationship: char.relationship || '',
      characterType: char.characterType || protagonistType,
    });
    setEditingId(char.id);
    setIsAdding(true);
  };

  const MAX_SUPPORTING = 3;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700">配角（可选）</h3>
        <span className="text-xs text-slate-400">{characters.length}/{MAX_SUPPORTING}</span>
      </div>

      {/* 配角列表 */}
      <div className="space-y-2 mb-3">
        {characters.map((char) => (
          <motion.div
            key={char.id}
            layout
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-50 rounded-xl p-3 border border-slate-100"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{char.name}</span>
                  <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">{char.species}</span>
                  {char.characterType && (
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">
                      {char.characterType === 'human' ? '人物' : '动物'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{char.appearance}</p>
                {char.relationship && (
                  <p className="text-xs text-slate-400 mt-0.5">{char.relationship}</p>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  onClick={() => handleEdit(char)}
                  className="text-xs text-indigo-500 hover:text-indigo-700 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                >
                  编辑
                </button>
                <button
                  onClick={() => onRemove(char.id)}
                  className="text-xs text-rose-500 hover:text-rose-700 px-2 py-1 rounded hover:bg-rose-50 transition-colors"
                >
                  删除
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* 添加按钮 */}
      {!isAdding && characters.length < MAX_SUPPORTING && (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50/30 transition-all text-sm"
        >
          <span className="text-lg">+</span>
          添加配角
        </button>
      )}

      {/* 添加/编辑表单 */}
      {isAdding && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-3"
        >
          {/* 角色类型 */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">角色类型 <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'human', label: '人物', emoji: '👧' },
                { id: 'animal', label: '动物', emoji: '🐰' },
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => setFormData((d) => ({ ...d, characterType: type.id as 'human' | 'animal', species: '' }))}
                  className={`
                    flex items-center gap-2 rounded-lg py-2 text-xs transition-all border
                    ${formData.characterType === type.id
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                      : 'bg-slate-50 border-slate-100 text-slate-500 hover:border-slate-200'
                    }
                  `}
                >
                  <span className="text-lg">{type.emoji}</span>
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 物种 / 身份 */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              {formData.characterType === 'human' ? '身份' : '物种'} <span className="text-red-500">*</span>
            </label>
            {formData.characterType === 'animal' ? (
              <>
                <div className="grid grid-cols-4 gap-1.5">
                  {SPECIES_OPTIONS.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setFormData((d) => ({ ...d, species: s.id }))}
                      className={`
                        flex flex-col items-center gap-0.5 rounded-lg py-1.5 text-xs transition-all
                        ${formData.species === s.id
                          ? 'bg-amber-50 border border-amber-200 text-amber-700'
                          : 'bg-slate-50 border border-slate-100 text-slate-500 hover:border-slate-200'
                        }
                      `}
                    >
                      <span>{s.emoji}</span>
                      <span>{s.label}</span>
                    </button>
                  ))}
                </div>
                {formData.species === 'custom' && (
                  <input
                    type="text"
                    value={formData.customSpecies}
                    onChange={(e) => setFormData((d) => ({ ...d, customSpecies: e.target.value }))}
                    placeholder="请输入物种"
                    className="w-full mt-1.5 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                )}
              </>
            ) : (
              <input
                type="text"
                value={formData.species}
                onChange={(e) => setFormData((d) => ({ ...d, species: e.target.value }))}
                placeholder="如：小男孩、妈妈、老师"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            )}
          </div>

          {/* 名字 */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">名字 <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
              placeholder={formData.characterType === 'human' ? '如：小明' : '如：小狐狸'}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          {/* 外貌描述 */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">外貌描述 <span className="text-red-500">*</span></label>
            <textarea
              value={formData.appearance}
              onChange={(e) => setFormData((d) => ({ ...d, appearance: e.target.value }))}
              placeholder="描述这个配角的外貌，如：橙色皮毛，尖尖的耳朵，机灵的大眼睛..."
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
              style={{ minHeight: '64px' }}
            />
          </div>

          {/* 关系 */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">与主角的关系</label>
            <input
              type="text"
              value={formData.relationship}
              onChange={(e) => setFormData((d) => ({ ...d, relationship: e.target.value }))}
              placeholder="如：主角的好朋友"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={!formData.name.trim() || !(formData.characterType === 'animal' && formData.species === 'custom' ? formData.customSpecies.trim() : formData.species.trim()) || !formData.appearance.trim()}
              className="flex-1 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {editingId ? '保存修改' : '添加'}
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 transition-colors"
            >
              取消
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
